import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Bell, DollarSign, Users, MapPin, Key, Webhook, Settings } from 'lucide-react';
import { WebhookSettings } from './WebhookSettings';
import { AlertThresholds, AlertThresholdConfig, DEFAULT_THRESHOLDS } from './AlertThresholds';
import { ProgramLimits } from './ProgramLimits';
import { ProcedureRates } from './ProcedureRates';
import { StaffPayRates } from './StaffPayRates';
import { APIAccess } from './APIAccess';
import { LocationManager } from '../Location/LocationManager';
import { TeamManager } from '../Team/TeamManager';
import { WebhookConfig } from '../../utils/webhook';
import { FeatureLock } from '../PlanGate/FeatureLock';
import { LTSSService } from '../../types/ltss';

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

function CollapsibleSection({ title, icon, defaultOpen = false, children }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-blue-400">{icon}</span>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 border-t border-slate-700">
          {children}
        </div>
      )}
    </div>
  );
}

interface SettingsPanelProps {
  webhookConfig: WebhookConfig;
  webhookLoading: boolean;
  webhookLastResult: { success: boolean; error?: string; timestamp?: Date } | null;
  onUpdateWebhookConfig: (config: Partial<WebhookConfig>) => void;
  onTestWebhook: () => Promise<void>;
  onUpgradeClick?: () => void;
  knownStaffNames?: string[];
  services?: LTSSService[];
}

const ALERT_THRESHOLDS_KEY = 'ltss_alert_thresholds';

export function SettingsPanel({
  webhookConfig,
  webhookLoading,
  webhookLastResult,
  onUpdateWebhookConfig,
  onTestWebhook,
  onUpgradeClick,
  knownStaffNames = [],
  services = [],
}: SettingsPanelProps) {
  const [alertThresholds, setAlertThresholds] = useState<AlertThresholdConfig>(DEFAULT_THRESHOLDS);

  // Load saved thresholds
  useEffect(() => {
    const saved = localStorage.getItem(ALERT_THRESHOLDS_KEY);
    if (saved) {
      try {
        setAlertThresholds(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load alert thresholds:', e);
      }
    }
  }, []);

  const handleSaveThresholds = (config: AlertThresholdConfig) => {
    setAlertThresholds(config);
    localStorage.setItem(ALERT_THRESHOLDS_KEY, JSON.stringify(config));
  };

  return (
    <div className="space-y-4">
      {/* Webhook Settings - Pro and above (most commonly used) */}
      <FeatureLock feature="canUseWebhooks" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="Webhook Integration" icon={<Webhook className="w-5 h-5" />} defaultOpen>
          <div className="pt-4">
            <WebhookSettings
              config={webhookConfig}
              isLoading={webhookLoading}
              lastResult={webhookLastResult}
              onUpdateConfig={onUpdateWebhookConfig}
              onTestWebhook={onTestWebhook}
            />
          </div>
        </CollapsibleSection>
      </FeatureLock>

      {/* Alert Thresholds - Pro and above */}
      <FeatureLock feature="canCustomizeAlerts" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="Alert Thresholds" icon={<Bell className="w-5 h-5" />}>
          <div className="pt-4">
            <AlertThresholds 
              config={alertThresholds} 
              onSave={handleSaveThresholds} 
            />
          </div>
        </CollapsibleSection>
      </FeatureLock>

      {/* Program Billing Limits - Pro and above */}
      <FeatureLock feature="canCustomizeAlerts" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="Program Billing Limits" icon={<Settings className="w-5 h-5" />}>
          <div className="pt-4">
            <ProgramLimits />
          </div>
        </CollapsibleSection>
      </FeatureLock>

      {/* Procedure Code Rates - Pro and above */}
      <FeatureLock feature="canViewRevenueLeak" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="Procedure Code Rates" icon={<DollarSign className="w-5 h-5" />}>
          <div className="pt-4">
            <ProcedureRates services={services} />
          </div>
        </CollapsibleSection>
      </FeatureLock>

      {/* Staff Pay Rates - Pro and above */}
      <FeatureLock feature="canCustomizeAlerts" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="Staff Pay Rates" icon={<DollarSign className="w-5 h-5" />}>
          <div className="pt-4">
            <StaffPayRates knownStaffNames={knownStaffNames} />
          </div>
        </CollapsibleSection>
      </FeatureLock>

      {/* Multi-location Management - Enterprise only */}
      <FeatureLock feature="hasMultiLocation" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="Multi-Location Management" icon={<MapPin className="w-5 h-5" />}>
          <div className="pt-4">
            <LocationManager />
          </div>
        </CollapsibleSection>
      </FeatureLock>

      {/* Team Management - Enterprise only */}
      <FeatureLock feature="hasTeamAccounts" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="Team Management" icon={<Users className="w-5 h-5" />}>
          <div className="pt-4">
            <TeamManager isModal={false} />
          </div>
        </CollapsibleSection>
      </FeatureLock>

      {/* API Access - Enterprise only */}
      <FeatureLock feature="canAccessAPI" onUpgradeClick={onUpgradeClick}>
        <CollapsibleSection title="API Access" icon={<Key className="w-5 h-5" />}>
          <div className="pt-4">
            <APIAccess />
          </div>
        </CollapsibleSection>
      </FeatureLock>
    </div>
  );
}
